import { useEffect, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { Trans } from '@lingui/react/macro';

import { ArticlesOfAssociation } from './RequirementTypes/ArticlesOfAssociation/ArticlesOfAssociation';
import { Bwa } from './RequirementTypes/Bwa/Bwa';
import { BwaSusa } from './RequirementTypes/BwaSusa/BwaSusa';
import { BwaSusaAnnual } from './RequirementTypes/BwaSusaAnnual/BwaSusaAnnual';
import { ShareRegistry } from './RequirementTypes/ShareRegistry/ShareRegistry';
import { TradeCertificate } from './RequirementTypes/TradeCertificate/TradeCertificate';
import styles from './TradingHistoryDE.module.css';
import { useOffersDE } from '../../../hooks/useOffers/useOffersDE';
import { TOfferDataRequirement } from '../../../types';
import { getAmplitudeDefaultEventProperties } from '../../../utils/amplitudeEvents';
import ApplicationLoader from '../../ApplicationLoader/ApplicationLoader';
import SaveProgressButton from '../../SaveProgressButton/SaveProgressButton';
import { OptionalRequirementsStatement } from '../OptionalRequirementsStatement/OptionalRequirementsStatement';
import BankStatementOpenBanking from '../SharedRequirementTypes/BankStatements/BankStatementsOpenBanking';
import SubmitDocumentsButtonDE from '../SubmitDocumentsButton/SubmitDocumentsButtonDE/SubmitDocumentsButtonDE';

const TradingHistoryDE = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);

  const offersInformation = useOffersDE();
  const documentRequirements = offersInformation.documentRequirements;

  useEffect(() => {
    tracking.ampli.stepLoaded({
      'Step name': 'Trading history',
      ...getAmplitudeDefaultEventProperties(),
    });
  }, []);

  if (offersInformation.isLoadingOrRedirecting) {
    return (
      <ApplicationLoader
        showMarketingPoll={offersInformation.isInExitPollTest}
      />
    );
  }

  if (!documentRequirements) {
    return null;
  }

  const DOCUMENT_REQUIREMENTS: { [key: string]: React.ElementType } = {
    kontoauszuege_hochladen: BankStatementOpenBanking,
    bwa_hochladen: Bwa,
    bwa_susa_hochladen: BwaSusa,
    jaehrliche_bwa_susa_hochladen: BwaSusaAnnual,
    gewerbeschein_hochladen: TradeCertificate,
    gesellschaftsvertrag_hochladen: ArticlesOfAssociation,
    aktienregisterauszug_hochladen: ShareRegistry,
  };

  const renderDocumentRequirements = (
    requirements: TOfferDataRequirement['actions'],
  ) => {
    return requirements?.map((requirement) => {
      if (!DOCUMENT_REQUIREMENTS[requirement.type]) {
        return null;
      }

      const RequirementDetails = DOCUMENT_REQUIREMENTS[requirement.type];

      return (
        <RequirementDetails
          key={requirement.type}
          isOptional={requirement.is_optional}
          quantifier={requirement.quantifier?.value}
          resolutionMethods={requirement.resolution_methods}
          status={requirement.status}
          isSubmitted={isSubmitted}
        />
      );
    });
  };

  const optionalDocumentRequirements = documentRequirements.filter(
    (document) => document.is_optional,
  );

  const requiredDocumentRequirements = documentRequirements?.filter(
    (document) => !document.is_optional,
  );

  return (
    <div className={styles.tradingHistory}>
      <div className={styles.headingContainer}>
        <h1 className={styles.heading}>
          <Trans>Add your trading history</Trans>
        </h1>
      </div>
      <div className={styles.documentsContainer}>
        <div>{renderDocumentRequirements(requiredDocumentRequirements)}</div>
        {optionalDocumentRequirements.length > 0 ? (
          <>
            <OptionalRequirementsStatement />
            <div>
              {renderDocumentRequirements(optionalDocumentRequirements)}
            </div>
          </>
        ) : null}
      </div>
      <div className={styles.buttonContainer}>
        <SaveProgressButton />
        <SubmitDocumentsButtonDE
          isSubmitted={isSubmitted}
          setIsSubmitted={setIsSubmitted}
        />
      </div>
    </div>
  );
};

export default TradingHistoryDE;
